'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import type { Me } from '@doughdog/types';
import { useKindeBrowserClient } from '@kinde-oss/kinde-auth-nextjs';

import { AuthState, AuthStatus } from '@/types';
import { writeAccessToken } from '@/utils/access-token';
import { LAST_SHOPIFY_APP_HANDLE_KEY, LAST_SHOPIFY_DOMAIN_KEY } from '@/utils/auth-keys';
import { resolveBearer } from '@/utils/bearer';
import { refreshAccessToken } from '@/utils/refresh';
import { safeSessionSet } from '@/utils/session-storage';

interface AuthContextValue {
	state: AuthState;
	refresh: () => Promise<void>;
	getToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const adoptAccessTokenFromHash = (): void => {
	if (typeof window === 'undefined') {
		return;
	}

	const token = new URLSearchParams(window.location.hash.slice(1)).get('access_token');

	if (!token) {
		return;
	}

	writeAccessToken(token);
};

const cacheShopifyDomain = (me: Me): void => {
	safeSessionSet(LAST_SHOPIFY_DOMAIN_KEY, me.shopifyDomain);

	// Cache the per-store app handle so a failed-refresh bounce targets the
	// right Shopify app. `shopifyAppHandle` is optional on the published Me
	// type for now; read it defensively until the types package is bumped.
	const handle = (me as Me & { shopifyAppHandle?: string | null }).shopifyAppHandle;

	if (handle) {
		safeSessionSet(LAST_SHOPIFY_APP_HANDLE_KEY, handle);
	}
};

type MeResult = { ok: true; me: Me } | { ok: false; status: number };

// Never throws: network/parse failures collapse to `status: 0` so callers can
// treat them like any other non-2xx (status 0 is retried as a transient error).
const fetchMe = async (bearer: string | null): Promise<MeResult> => {
	const headers: Record<string, string> = { Accept: 'application/json' };

	if (bearer) {
		headers.Authorization = `Bearer ${bearer}`;
	}

	try {
		const res = await fetch(`${process.env.NEXT_PUBLIC_DOUGH_DOG_API}/me`, { headers });

		if (!res.ok) {
			return { ok: false, status: res.status };
		}

		return { me: (await res.json()) as Me, ok: true };
	} catch {
		return { ok: false, status: 0 };
	}
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const kinde = useKindeBrowserClient();
	const [state, setState] = useState<AuthState>({ status: AuthStatus.Loading });

	const getToken = useCallback((): string | null => {
		return kinde.getToken();
	}, [kinde]);

	const refresh = useCallback(async () => {
		// If we just landed from the Shopify install handoff, the access token is
		// sitting in the URL fragment. Copy it into sessionStorage before we
		// decide which Bearer to attach.
		adoptAccessTokenFromHash();

		const { identity, token } = await resolveBearer(kinde);

		let result = await fetchMe(token);

		if (!result.ok && (result.status === 0 || result.status >= 500)) {
			result = await fetchMe(token);
		}

		// Merchant (non-Kinde) sessions: if there's no usable access token, or the
		// one we have is expired/rejected, exchange the HttpOnly refresh cookie for
		// a fresh access token and retry once. This keeps a returning merchant
		// signed in (new tab → empty sessionStorage, or an expired 1h token) via
		// their 30-day refresh cookie, instead of dropping straight to Anonymous.
		//
		// Gate on auth failures only. A denied API Gateway Lambda authorizer
		// returns 403 (and the no-token case lands here too), while an expired
		// token yields 401 — refreshing fixes both. Don't refresh on 5xx (already
		// retried above): a new token can't fix a server error, so it's a wasted
		// round-trip on every outage.
		const isAuthFailure = !result.ok && (result.status === 401 || result.status === 403);

		if (identity !== 'kinde' && isAuthFailure) {
			const refreshed = await refreshAccessToken();

			if (refreshed) {
				result = await fetchMe(refreshed);
			}
		}

		if (result.ok) {
			// `source` is optional on Me; fall back to the resolved identity so a
			// validly authenticated user is never dropped to Anonymous over a
			// missing field.
			const source = result.me.source ?? (identity === 'kinde' ? 'kinde' : 'shopify');
			cacheShopifyDomain(result.me);
			setState({ me: result.me, source, status: AuthStatus.Authenticated });
			return;
		}

		setState({ status: AuthStatus.Anonymous });
	}, [kinde]);

	useEffect(() => {
		if (kinde.isLoading) {
			return;
		}

		// refresh() only calls setState after an awaited /me fetch, never synchronously, so
		// this is the allowed "sync external auth state on initial load" effect — not a
		// cascading-render setState. The rule can't see through the memoized callback.
		// eslint-disable-next-line react-hooks/set-state-in-effect
		void refresh();
		// Re-run only when Kinde finishes its initial load; consumers call refresh() explicitly.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kinde.isLoading]);

	return <AuthContext.Provider value={{ getToken, refresh, state }}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
	const ctx = useContext(AuthContext);

	if (!ctx) {
		throw new Error('useAuth must be used within AuthProvider');
	}
	return ctx;
};
